import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    formation: '',
    message: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Création du contenu de l'email
    const emailSubject = encodeURIComponent('Contact - École de la Pizza italienne');
    const emailBody = encodeURIComponent(
      `Bonjour,\n\n` +
      `Vous avez reçu un nouveau message de contact :\n\n` +
      `NOM : ${formData.nom}\n` +
      `PRÉNOM : ${formData.prenom}\n` +
      `EMAIL : ${formData.email}\n` +
      `TÉLÉPHONE : ${formData.telephone}\n` +
      `FORMATION D'INTÉRÊT : ${formData.formation}\n\n` +
      `MESSAGE :\n${formData.message}\n\n` +
      `Cordialement`
    );
    
    // Ouverture du client email
    const mailtoLink = `mailto:ecoledelapizza@gmail.com?subject=${emailSubject}&body=${emailBody}`;
    window.location.href = mailtoLink;
    
    // Message de confirmation
    alert('Votre client email va s\'ouvrir avec votre message pré-rempli. Envoyez l\'email pour nous contacter.');
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Contactez-nous
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Une question sur nos formations ? Besoin d'un conseil personnalisé ? 
            Notre équipe est là pour vous accompagner dans votre projet.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Informations de Contact */}
          <div className="space-y-8">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">Informations de Contact</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-red-100 p-3 rounded-lg">
                    <Phone className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Téléphone</h4>
                    <p className="text-gray-600">06 60 47 67 40</p>
                    <p className="text-sm text-gray-500">Lun-Ven: 9h-18h</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-red-100 p-3 rounded-lg">
                    <Mail className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                    <a 
                      href="mailto:ecoledelapizza@gmail.com"
                      className="text-red-600 hover:text-red-700 transition-colors"
                    >
                      ecoledelapizza@gmail.com
                    </a>
                    <p className="text-sm text-gray-500">Réponse sous 24h</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-red-100 p-3 rounded-lg">
                    <MapPin className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Adresse</h4>
                    <p className="text-gray-600">2720 Chemin Saint Bernard</p>
                    <p className="text-gray-600">Porte 14 bâtiment Sodimats</p>
                    <p className="text-gray-600">06220 Vallauris, France</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-red-100 p-3 rounded-lg">
                    <Clock className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Horaires</h4>
                    <p className="text-gray-600">Lundi - Vendredi: 9h - 18h</p>
                    <p className="text-gray-600">Samedi: 9h - 12h</p>
                    <p className="text-gray-600">Dimanche: Fermé</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Formateur */}
            <div className="bg-gray-50 rounded-2xl p-6">
              <h4 className="text-xl font-bold text-gray-900 mb-4">Votre Formateur</h4>
              <div className="flex items-center gap-4">
                <img 
                  src="./images/chef_formation_1.jpeg" 
                  alt="Joseph Cardone"
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div>
                  <h5 className="font-semibold text-gray-900">Joseph Cardone</h5>
                  <p className="text-gray-600">Maître Pizzaïolo</p>
                  <p className="text-sm text-gray-500">30+ ans d'expérience</p>
                </div>
              </div>
              <p className="text-gray-600 mt-4">
                Expert reconnu de la pizza napolitaine, Joseph transmet sa passion et son savoir-faire 
                avec authenticité et professionnalisme.
              </p>
            </div>
          </div>

          {/* Formulaire de Contact */}
          <div className="bg-gray-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Envoyez-nous un Message</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="prenom" className="block text-sm font-medium text-gray-700 mb-2">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    id="prenom"
                    name="prenom"
                    required
                    value={formData.prenom}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-2">
                    Nom *
                  </label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    required
                    value={formData.nom}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 mb-2">
                  Téléphone
                </label>
                <input
                  type="tel"
                  id="telephone"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="formation" className="block text-sm font-medium text-gray-700 mb-2">
                  Formation d'intérêt
                </label>
                <select
                  id="formation"
                  name="formation"
                  value={formData.formation}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="">Sélectionnez une formation</option>
                  <option value="expert-napolitaine">Expert Pizza Napolitaine</option>
                  <option value="pizzaiolo-base">Formation Pizzaïolo</option>
                  <option value="cqp">CQP Pizzaïolo</option>
                  <option value="haccp">Hygiène Alimentaire HACCP</option>
                  <option value="creation-entreprise">Création d'Entreprise</option>
                  <option value="atelier-particulier">Atelier Particulier</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Décrivez votre projet, vos questions ou vos besoins..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full bg-red-600 text-white py-4 rounded-lg hover:bg-red-700 transition-colors font-semibold text-lg flex items-center justify-center gap-2"
              >
                <Send className="h-5 w-5" />
                Envoyer le Message
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;